import {Checkbox, FormControl, useFormContext} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {Path, useController, useWatch} from 'react-hook-form';

import {FieldDefinitionResponseBody, StageSequenceDefinitionResponseBody} from '@dms/api/settings';

import {MandatoryFieldsRequestFormSchemaType} from '../schemas/MandatoryFieldsFormSchema';
import {MandatoryFieldsItemSequence} from './MandatoryFieldsItemSequence';

interface MandatoryFieldsItemProps {
  field: FieldDefinitionResponseBody;
  stageSequences: StageSequenceDefinitionResponseBody[];
  control: FormControl<MandatoryFieldsRequestFormSchemaType>;
  defaultValues: Partial<MandatoryFieldsRequestFormSchemaType>;
}

export function MandatoryFieldsItem(props: MandatoryFieldsItemProps) {
  const fieldName =
    `fields.${props.field.id}.isEnabled` as Path<MandatoryFieldsRequestFormSchemaType>;

  const {field: isEnabledField} = useController({
    name: fieldName,
    control: props.control,
  });

  const {setValue} = useFormContext<MandatoryFieldsRequestFormSchemaType>();

  const isEnabled = useWatch({
    control: props.control,
    name: fieldName,
  });

  useEffect(
    function clearStagesWhenDisabled() {
      if (!isEnabled) {
        const stagePath =
          `fields.${props.field.id}.stages` as Path<MandatoryFieldsRequestFormSchemaType>;
        setValue(stagePath, {}, {shouldDirty: true});
      }
    },
    [isEnabled, props.control] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return (
    <VStack spacing={4}>
      <HStack spacing={3}>
        <Checkbox
          label={props.field.name}
          value={!!isEnabledField.value}
          onChange={(value) => isEnabledField.onChange(value)}
          name={fieldName}
        />
      </HStack>

      <Show when={!!isEnabledField.value}>
        <HStack spacing={4}>
          {props.stageSequences.map((stageSequence) => (
            <MandatoryFieldsItemSequence
              key={stageSequence.id}
              stageSequence={stageSequence}
              fieldId={props.field.id}
              control={props.control}
              defaultValues={props.defaultValues}
            />
          ))}
        </HStack>
      </Show>
    </VStack>
  );
}
